import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import AthleteAvatar from '../components/AthleteAvatar';
import SearchableAthleteSelect from '../components/SearchableAthleteSelect';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { ClipboardList, Video, Activity, ChevronLeft, ChevronRight, History } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const filtersFromSearch = (sp) => ({
  match_source: sp.get('match_source') || 'all',
  competition: sp.get('competition') || 'all',
  verification_status: sp.get('verification_status') || 'all',
  opponent: sp.get('opponent') || '',
  season_scope: sp.get('season_scope') || 'current',
  has_analysis: sp.get('has_analysis') || 'all',
  has_gps: sp.get('has_gps') || 'all',
});

const makeHistorySearch = (athleteId, filters, page) => {
  const next = new URLSearchParams();
  if (athleteId) next.set('athlete', athleteId);
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== '' && value !== 'all' && !(key === 'season_scope' && value === 'current')) next.set(key, value);
  });
  if (page > 0) next.set('page', String(page));
  return next;
};

const RatingBadge = ({ rating }) => {
  if (rating == null) return <span className="text-gray-400">—</span>;
  const cls = rating >= 8 ? 'bg-emerald-100 text-emerald-800'
    : rating >= 6 ? 'bg-blue-100 text-blue-800'
    : rating >= 4 ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800';
  return <Badge className={cls}>{rating}/10</Badge>;
};

const StatTile = ({ label, value, testid, sub }) => (
  <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-3 text-center" data-testid={testid}>
    <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{value ?? '—'}</p>
    <p className="text-xs text-gray-500 dark:text-gray-500">{label}</p>
    {sub && <p className="text-[10px] text-gray-400 mt-0.5">{sub}</p>}
  </div>
);

const AthleteMatchHistoryPage = () => {
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();

  const [athletes, setAthletes] = useState([]);
  const [selectedAthlete, setSelectedAthlete] = useState(sp.get('athlete') || '');
  const [config, setConfig] = useState(null);
  const [filters, setFilters] = useState(() => filtersFromSearch(sp));
  const [summary, setSummary] = useState(null);
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(() => Math.max(0, Number(sp.get('page')) || 0));
  const LIMIT = 20;

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/athletes`),
      axios.get(`${API}/matches-v3/config`),
    ]).then(([a, c]) => {
      const list = Array.isArray(a.data) ? a.data : (a.data?.athletes || []);
      setAthletes(list);
      setConfig(c.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    setSp(makeHistorySearch(selectedAthlete, filters, page), { replace: true });
  }, [selectedAthlete, filters, page, setSp]);

  const buildParams = useCallback((extra = {}) => {
    const p = {};
    if (filters.match_source !== 'all') p.match_source = filters.match_source;
    if (filters.competition !== 'all') p.competition = filters.competition;
    if (filters.verification_status !== 'all') p.verification_status = filters.verification_status;
    if (filters.opponent) p.opponent = filters.opponent;
    if (filters.season_scope !== 'all') p.season_scope = filters.season_scope;
    if (filters.has_analysis !== 'all') p.has_analysis = filters.has_analysis === 'yes';
    if (filters.has_gps !== 'all') p.has_gps = filters.has_gps === 'yes';
    return { ...p, ...extra };
  }, [filters]);

  const fetchData = useCallback(async (athleteId, pageIdx) => {
    if (!athleteId) return;
    setLoading(true);
    try {
      const [sRes, hRes] = await Promise.all([
        axios.get(`${API}/athlete-match-reports/${athleteId}/summary`, { params: buildParams() }),
        axios.get(`${API}/athlete-match-reports/${athleteId}/history`, {
          params: buildParams({ limit: LIMIT, offset: pageIdx * LIMIT }),
        }),
      ]);
      setSummary(sRes.data);
      setHistory(hRes.data);
    } catch (e) {
      if (e.response?.status === 403) toast.error('You do not have permission to view this athlete.');
      else toast.error('Failed to load match history');
      setSummary(null);
      setHistory(null);
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  useEffect(() => {
    if (selectedAthlete) fetchData(selectedAthlete, page);
    else {
      setSummary(null);
      setHistory(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAthlete, filters, page]);

  const updateFilter = (key, value) => {
    setPage(0);
    setFilters(current => ({ ...current, [key]: value }));
  };

  const onSelectAthlete = (id) => {
    setPage(0);
    setSelectedAthlete(id);
  };

  const openMatch = (matchId) => {
    const returnTo = `/match-reports?${makeHistorySearch(selectedAthlete, filters, page).toString()}`;
    navigate(`/match-reports/${matchId}?athlete=${selectedAthlete}&returnTo=${encodeURIComponent(returnTo)}`);
  };

  const totals = summary?.totals;
  const gps = summary?.gps_summary;
  const items = history?.items || [];
  const unresolved = history?.unresolved_legacy_observations || [];
  const totalPages = history ? Math.ceil((history.total || 0) / LIMIT) : 0;

  const competitionOptions = useMemo(() => {
    const set = new Set([...(config?.pfap_competitions || []), ...(config?.external_competitions || [])]);
    return Array.from(set);
  }, [config]);

  return (
    <Layout>
      <div className="p-4 sm:p-6 lg:p-8" data-testid="athlete-match-history-page">
        <div className="mb-6 flex items-center gap-3">
          <History className="w-8 h-8 text-pfap-green hidden sm:block" />
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Match Reports
            </h2>
            <p className="text-gray-600 dark:text-gray-500 mt-1 text-sm">
              Athlete match history, cumulative statistics and complete match development reports
            </p>
          </div>
        </div>

        <Card className="mb-6">
          <CardContent className="pt-6 space-y-4">
            <div>
              <Label>Select Athlete</Label>
              <SearchableAthleteSelect
                athletes={athletes}
                value={selectedAthlete}
                onChange={onSelectAthlete}
                placeholder="Choose an athlete..."
                data-testid="mrv2-athlete-select"
              />
            </div>

            {selectedAthlete && (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="mrv2-filters">
                <div>
                  <Label className="text-xs">Source</Label>
                  <Select value={filters.match_source} onValueChange={v => updateFilter('match_source', v)}>
                    <SelectTrigger data-testid="filter-source"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      <SelectItem value="PFAP">PFAP</SelectItem>
                      <SelectItem value="External">External</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Competition</Label>
                  <Select value={filters.competition} onValueChange={v => updateFilter('competition', v)}>
                    <SelectTrigger data-testid="filter-competition"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      {competitionOptions.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Verification</Label>
                  <Select value={filters.verification_status} onValueChange={v => updateFilter('verification_status', v)}>
                    <SelectTrigger data-testid="filter-verification"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      <SelectItem value="Verified">Verified</SelectItem>
                      <SelectItem value="Pending">Pending</SelectItem>
                      <SelectItem value="Rejected">Rejected</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Season</Label>
                  <Select value={filters.season_scope} onValueChange={v => updateFilter('season_scope', v)}>
                    <SelectTrigger data-testid="filter-season-scope"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Full History</SelectItem>
                      <SelectItem value="current">Current Season</SelectItem>
                      <SelectItem value="previous">Previous Season</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Has Analysis</Label>
                  <Select value={filters.has_analysis} onValueChange={v => updateFilter('has_analysis', v)}>
                    <SelectTrigger data-testid="filter-has-analysis"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      <SelectItem value="yes">Yes</SelectItem>
                      <SelectItem value="no">No</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Has GPS</Label>
                  <Select value={filters.has_gps} onValueChange={v => updateFilter('has_gps', v)}>
                    <SelectTrigger data-testid="filter-has-gps"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      <SelectItem value="yes">Yes</SelectItem>
                      <SelectItem value="no">No</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2 sm:col-span-3 lg:col-span-6">
                  <Label className="text-xs">Opponent</Label>
                  <Input placeholder="Search opponent..." value={filters.opponent}
                         onChange={e => updateFilter('opponent', e.target.value)}
                         data-testid="filter-opponent" />
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {!selectedAthlete ? (
          <Card><CardContent className="py-16 text-center text-gray-500 dark:text-gray-500" data-testid="mrv2-no-athlete">
            Select an athlete to view their match history.
          </CardContent></Card>
        ) : (
          <>
            {totals && (
              <Card className="mb-6" data-testid="mrv2-summary">
                <CardHeader className="pb-2 flex flex-row items-center gap-3">
                  <AthleteAvatar name={summary.athlete?.full_name} size="sm" showName />
                  <CardTitle className="text-sm text-gray-400 font-normal">Cumulative Statistics</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 sm:grid-cols-6 gap-3 mb-3">
                    <StatTile label="Matches" value={totals.matches} testid="stat-matches" />
                    <StatTile label="Minutes" value={totals.minutes} testid="stat-minutes" />
                    <StatTile label="Goals" value={totals.goals} testid="stat-goals" sub={totals.goals_per90 != null ? `${totals.goals_per90}/90` : null} />
                    <StatTile label="Assists" value={totals.assists} testid="stat-assists" sub={totals.assists_per90 != null ? `${totals.assists_per90}/90` : null} />
                    <StatTile label="Avg Rating" value={totals.average_rating} testid="stat-rating"
                               sub={`${totals.coverage.rating.recorded}/${totals.coverage.rating.total} matches`} />
                    <StatTile label="Cards" value={`${totals.yellow_cards}Y / ${totals.red_cards}R`} testid="stat-cards" />
                  </div>
                  {(totals.pass_completion_pct != null || totals.tackle_success_pct != null) && (
                    <div className="flex gap-2 flex-wrap mb-2">
                      {totals.pass_completion_pct != null && (
                        <Badge variant="outline" data-testid="stat-pass-completion">
                          Pass completion: {totals.pass_completion_pct}% ({totals.coverage.technical.recorded}/{totals.coverage.technical.total} matches)
                        </Badge>
                      )}
                      {totals.tackle_success_pct != null && (
                        <Badge variant="outline" data-testid="stat-tackle-success">
                          Tackle success: {totals.tackle_success_pct}% ({totals.coverage.defensive.recorded}/{totals.coverage.defensive.total} matches)
                        </Badge>
                      )}
                    </div>
                  )}
                  {totals.technical === null && totals.defensive === null && (
                    <p className="text-xs text-gray-400" data-testid="stat-no-technical">
                      No detailed technical/defensive statistics recorded for the selected matches.
                    </p>
                  )}
                  {gps && gps.matches_with_gps > 0 && (
                    <div className="mt-3 pt-3 border-t flex flex-wrap gap-4 text-sm" data-testid="stat-gps-summary">
                      <span className="flex items-center gap-1"><Activity className="w-3.5 h-3.5 text-emerald-600" />
                        GPS coverage: {gps.matches_with_gps}/{gps.matches_total} matches</span>
                      {gps.total_distance_km != null && <span>Total distance: {gps.total_distance_km} km</span>}
                      {gps.max_top_speed_kmh != null && <span>Max speed: {gps.max_top_speed_kmh} km/h</span>}
                      {gps.total_sprint_count != null && <span>Sprints: {gps.total_sprint_count}</span>}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            <Card>
              <CardHeader>
                <CardTitle>Match History</CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
                  </div>
                ) : items.length === 0 ? (
                  <div className="py-12 text-center text-gray-500 dark:text-gray-500" data-testid="mrv2-no-matches">
                    No matches found for the selected filters.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {items.map(row => (
                      <div key={row.participant_id}
                           className="p-3 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-100 dark:border-gray-700 hover:border-emerald-300 cursor-pointer transition-colors"
                           onClick={() => openMatch(row.match_v3_id)}
                           data-testid={`mrv2-match-row-${row.participant_id}`}>
                        <div className="flex items-center justify-between flex-wrap gap-2">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold text-gray-900 dark:text-gray-100">vs {row.opponent}</span>
                            <Badge variant="outline" className="text-xs">{row.match_source}</Badge>
                            {row.verification_status !== 'Verified' && (
                              <Badge className="bg-amber-100 text-amber-800 text-xs">{row.verification_status}</Badge>
                            )}
                            <RatingBadge rating={row.rating} />
                          </div>
                          <div className="flex items-center gap-3 text-xs text-gray-500">
                            <span className={`flex items-center gap-1 ${row.has_analysis ? 'text-blue-600' : 'text-gray-400'}`}
                                  data-testid={`ind-analysis-${row.participant_id}`} title="Analysis availability">
                              <ClipboardList className="w-3.5 h-3.5" /> {row.has_analysis ? row.analysis_count : 'None'}
                            </span>
                            <span className={`flex items-center gap-1 ${row.has_video ? 'text-sky-600' : 'text-gray-400'}`}
                                  data-testid={`ind-video-${row.participant_id}`} title="Video availability">
                              <Video className="w-3.5 h-3.5" /> {row.has_video ? row.video_count : 'None'}
                            </span>
                            <span className={`flex items-center gap-1 ${row.has_gps ? 'text-emerald-600' : 'text-gray-400'}`}
                                  data-testid={`ind-gps-${row.participant_id}`} title="GPS availability">
                              <Activity className="w-3.5 h-3.5" /> {row.has_gps ? 'GPS' : 'None'}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 text-sm text-gray-500 dark:text-gray-500 mt-1">
                          <span>{row.match_date}</span>
                          <span>{row.competition}</span>
                          <span>{row.position || '—'}</span>
                          <span>{row.minutes_played != null ? `${row.minutes_played} mins` : '—'}</span>
                          {row.result && <span>{row.result}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-3 mt-4">
                    <Button size="sm" variant="outline" disabled={page === 0} onClick={() => setPage(p => p - 1)} data-testid="mrv2-prev-page">
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <span className="text-sm text-gray-500">Page {page + 1} of {totalPages}</span>
                    <Button size="sm" variant="outline" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)} data-testid="mrv2-next-page">
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                )}

                {unresolved.length > 0 && (
                  <div className="mt-6 pt-4 border-t">
                    <p className="text-xs font-semibold uppercase text-gray-400 mb-2">Legacy Analysis — Match Link Unresolved</p>
                    {unresolved.map(o => (
                      <div key={o.id} className="p-3 bg-amber-50 dark:bg-amber-900/10 rounded-lg border border-amber-200 dark:border-amber-900/40 mb-2"
                           data-testid={`orphan-observation-${o.id}`}>
                        <p className="text-sm text-gray-700 dark:text-gray-300">{o.observation}</p>
                        <p className="text-xs text-gray-500 mt-1">{o.match_date} · {o.category} · not linked to a match record — excluded from aggregates.</p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </Layout>
  );
};

export default AthleteMatchHistoryPage;
